import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const TIMEFRAMES = ["M1", "M5", "M15", "M30", "H1", "H4", "D1", "W1"];

export const api = {
  symbols: () => axios.get(`${API}/symbols`).then((r) => r.data),
  analyze: (symbol, timeframe) =>
    axios.get(`${API}/analyze`, { params: { symbol, timeframe } }).then((r) => r.data),
  analyzeAndNotify: (symbol, timeframe, notify, filters) =>
    axios.post(`${API}/analyze`, { symbol, timeframe, notify, filters }).then((r) => r.data),
  watchlist: () => axios.get(`${API}/watchlist`).then((r) => r.data),
  watchlistQuotes: (symbols, timeframe = "H1") =>
    axios.get(`${API}/watchlist/quotes`, { params: { symbols: symbols.join(","), timeframe } }).then((r) => r.data),
  settings: () => axios.get(`${API}/settings`).then((r) => r.data),
  updateSettings: (data) => axios.put(`${API}/settings`, data).then((r) => r.data),
  testNotify: (channel) => axios.post(`${API}/notify/test`, { channel }).then((r) => r.data),
  signals: () => axios.get(`${API}/signals`).then((r) => r.data),
  confluenceStats: () => axios.get(`${API}/signals/confluence-stats`).then((r) => r.data),
  updateSignalStatus: (id, status) => axios.post(`${API}/signals/${id}/status`, { status }).then((r) => r.data),
  // ---- Phase 7-9: Execution & Order Flow ----
  orderFlow: (symbol, timeframe = "H1", bars = 200) =>
    axios.get(`${API}/order-flow`, { params: { symbol, timeframe, bars } }).then((r) => r.data),
  submitOrder: (order) => axios.post(`${API}/execution/order`, order).then((r) => r.data),
  brokerAccount: () => axios.get(`${API}/execution/account`).then((r) => r.data),
  brokerPositions: () => axios.get(`${API}/execution/positions`).then((r) => r.data),
  brokerFills: (limit = 50) =>
    axios.get(`${API}/execution/fills`, { params: { limit } }).then((r) => r.data),
  brokerClosedTrades: (limit = 50) =>
    axios.get(`${API}/execution/closed-trades`, { params: { limit } }).then((r) => r.data),
  brokerReset: (equity = 100000) =>
    axios.post(`${API}/execution/reset`, { initial_equity: equity }).then((r) => r.data),
  tickBacktest: (symbol, timeframe = "H1", bars = 800, risk = 1.0) =>
    axios.get(`${API}/backtest/tick`, { params: { symbol, timeframe, bars, risk_pct: risk } }).then((r) => r.data),
  // ---- Risk & Compliance ----
  riskLimits: () => axios.get(`${API}/risk/limits`).then((r) => r.data),
  updateRiskLimits: (limits) => axios.put(`${API}/risk/limits`, { limits }).then((r) => r.data),
  killSwitch: () => axios.get(`${API}/risk/kill-switch`).then((r) => r.data),
  killSwitchReset: () => axios.post(`${API}/risk/kill-switch/reset`).then((r) => r.data),
  pnlAttribution: (symbol = "EURUSD", timeframe = "H1") =>
    axios.get(`${API}/pnl/attribution`, { params: { symbol, timeframe } }).then((r) => r.data),
  pnlDaily: (asOf) =>
    axios.get(`${API}/reporting/pnl-daily`, { params: asOf ? { as_of: asOf } : {} }).then((r) => r.data),
  // ---- Allocation ----
  regimeAllocation: (symbol = "EURUSD", timeframe = "H1") =>
    axios.get(`${API}/allocation/regime`, { params: { symbol, timeframe } }).then((r) => r.data),
  kellyMulti: (assets, correlations = {}, fraction = 0.25) =>
    axios.post(`${API}/allocation/kelly-multi`, {
      assets, correlations, kelly_fraction: fraction,
    }).then((r) => r.data),
  regimeHistory: () => axios.get(`${API}/allocation/regime-history`).then((r) => r.data),
  // ---- Phase 15-17 : Users, Auth, Onboarding ----
  signup: (email, password) =>
    axios.post(`${API}/users/signup`, { email, password }).then((r) => r.data),
  login: (email, password) =>
    axios.post(`${API}/users/login`, { email, password }).then((r) => r.data),
  me: () => axios.get(`${API}/users/me`).then((r) => r.data),
  completeOnboarding: () => axios.post(`${API}/users/onboarding`, { completed: true }).then((r) => r.data),
  funnelStats: () => axios.get(`${API}/users/analytics/funnel`).then((r) => r.data),
  walkForward: (returns, trainFrac = 0.6, stepFrac = 0.1) =>
    axios.post(`${API}/stats/walk-forward`, { returns, train_frac: trainFrac, step_frac: stepFrac }).then((r) => r.data),
  pbo: (returnsMatrix, nSplits = 8) =>
    axios.post(`${API}/stats/pbo`, { returns_matrix: returnsMatrix, n_splits: nSplits }).then((r) => r.data),
  brokerAdapters: () => axios.get(`${API}/broker/adapters`).then((r) => r.data),
  // ---- Reporting downloads (URLs) ----
  mifidReportUrl: `${API}/reporting/mifid`,
  emirReportUrl: `${API}/reporting/emir`,
  auditExportUrl: `${API}/reporting/audit-export`,
  // ---- Phase 11-14 ----
  l2ProviderInfo: () => axios.get(`${API}/l2/provider-info`).then((r) => r.data),
  l2Snapshot: (symbol = "EURUSD") =>
    axios.get(`${API}/l2/snapshot`, { params: { symbol } }).then((r) => r.data),
  l2RecentTrades: (symbol = "EURUSD", n = 20) =>
    axios.get(`${API}/l2/recent-trades`, { params: { symbol, n } }).then((r) => r.data),
  eventBacktest: (params = {}) =>
    axios.get(`${API}/backtest/event-driven`, { params }).then((r) => r.data),
  statsSignificance: (returns, nTrials = 100) =>
    axios.post(`${API}/stats/significance`, { returns, n_strategies_tested: nTrials }).then((r) => r.data),
  whitesRealityCheck: (strategies) =>
    axios.post(`${API}/stats/whites-reality-check`, { strategies_returns: strategies }).then((r) => r.data),
  mlTrain: (useSynthetic = true, nSynthetic = 500, purgeGap = 3) =>
    axios.post(`${API}/ml/train`, null, { params: { use_synthetic: useSynthetic, n_synthetic: nSynthetic, purge_gap: purgeGap } }).then((r) => r.data),
  mlModel: () => axios.get(`${API}/ml/model`).then((r) => r.data),
  mlPredictTrained: (features) => axios.post(`${API}/ml/predict-trained`, features).then((r) => r.data),
  killSwitchFlatten: () => axios.post(`${API}/risk/kill-switch/flatten`).then((r) => r.data),
  tiers: () => axios.get(`${API}/subscriptions/tiers`).then((r) => r.data),
  founderOffer: () => axios.get(`${API}/subscriptions/founder-offer`).then((r) => r.data),
  createCheckout: (tierId, originUrl, billingCycle = "monthly") =>
    axios.post(`${API}/subscriptions/checkout`, { tier_id: tierId, origin_url: originUrl, billing_cycle: billingCycle }).then((r) => r.data),
  checkoutStatus: (sessionId) =>
    axios.get(`${API}/subscriptions/status/${sessionId}`).then((r) => r.data),
};

export function trendChip(label) {
  if (label === "Haussière") return { cls: "chip-up", icon: "up" };
  if (label === "Baissière") return { cls: "chip-down", icon: "down" };
  return { cls: "chip-neutral", icon: "neutral" };
}
